import React, {useState, useEffect, useRef, useMemo} from 'react';
import PropTypes from 'prop-types';
import {FmlxButton, FmlxIcon, FmlxTooltip, FmlxDivider, FmlxPopover, FmlxList} from 'fmlx-common-ui';
import {connect} from 'react-redux';
import {bindActionCreators} from 'redux';

import {useClickOutside} from 'lib/customHooks';
import {PlateType, WellBottomProfile, WellCount} from '../../constants/dataEnum';

import withIntl from '../../lib/withIntl';
import {
  ListsOfPlateProperties,
  PLATE_PROPERTIES_VALUE_PRECISION,
  PlateProperties,
  TroughWellList,
  WellLabel,
} from './PlateTypeEditorEnum';
import FastTextBox from '../../components/Inputs/FastTextBox';
import {TextBoxMode} from '../../constants/UIComponentEnum';
import {startReteachPlate} from '../../stores/business/plateTypeEditorBusiness';
import {getPlatePropertyPlaceholder} from './plateTypeHelper';

function PlateViewItemProperty({
  label,
  value,
  onUpdate,
  editMode,
  disabled,
  isEditAllowed,
  placeholder,
  intl,
  precision,
}) {
  const isError = parseFloat(value) === 0 || value === '';

  return (
    <div className="plate-measurement-line-wrapper">
      <div className="plate-measurement-line">
        <div>
          <span>{label}</span>
          {editMode && <span style={{color: '#DB0000'}}>*</span>}
        </div>
        {!editMode ? (
          <div className="plate-measurement-value">{`${value} (mm)`}</div>
        ) : (
          <FmlxTooltip
            placement="bottom"
            title={
              disabled && isEditAllowed
                ? intl.formatMessage({id: 'plateTypeEditorContainer.tooltip.plateProperties.safeValue'})
                : ''
            }
          >
            <span>
              <FastTextBox
                className="plate-measurement-value-edit"
                placeholder={placeholder}
                value={value}
                onBlur={(newVal) => onUpdate(newVal)}
                disabled={disabled}
                mode={TextBoxMode.NUMBER}
                hidesArrowButton
                precision={precision}
              />
            </span>
          </FmlxTooltip>
        )}
      </div>
      {isError && (
        <span className="plate-measurement-error-message">
          {intl.formatMessage({id: 'plateTypeEditorContainer.editPlateProperties.errorMessage'})}
        </span>
      )}
    </div>
  );
}

PlateViewItemProperty.propTypes = {
  label: PropTypes.string.isRequired,
  value: PropTypes.number.isRequired,
  onUpdate: PropTypes.func.isRequired,
  disabled: PropTypes.bool.isRequired,
  editMode: PropTypes.bool.isRequired,
  intl: PropTypes.any.isRequired,
  isEditAllowed: PropTypes.bool.isRequired,
  placeholder: PropTypes.string,
  precision: PropTypes.number,
};

PlateViewItemProperty.defaultProps = {
  placeholder: '',
  precision: 1,
};

function PlateViewSummaryInfo({
  wellNumberImageName,
  wellNumber,
  wellLabel,
  wellBottomProfile,
  wellShape,
  plateType,
  height,
  depth,
  width,
  length,
  columnOrigin,
  rowOrigin,
  columnPitch,
  rowPitch,
  colPitchAssaySample,
  troughDepth,
  handleEditPlateProperties,
  setEditModePlateProperties,
  editModePlateProperties,
  isEdit,
  isError,
  setIsError,
  intl,
  onReteachPlate,
  isEditAllowed,
  loggedInUser,
  aboutState,
  onManualReteachPlate,
}) {
  const [platePropertiesValue, setPlatePropertiesValue] = useState({
    height,
    depth,
    width,
    length,
    columnOrigin,
    rowOrigin,
    columnPitch,
    rowPitch,
    colPitchAssaySample,
    troughDepth,
  });

  const ItemProperty = withIntl(PlateViewItemProperty);
  const contextMenuReteachRef = useRef(null);
  const [anchorElMenu, setAnchorElMenu] = useState(null);
  const [adding24Or48WellPlate, setAdding24Or48WellPlate] = useState(false);

  let classNameByWellNumber = '';
  let wellBottomPrefixName = wellNumber;

  switch (wellLabel) {
    case WellLabel.WELL_1:
      classNameByWellNumber = 'isOneWell';
      break;
    default:
      classNameByWellNumber = `is${wellNumberImageName}Well`;
      break;
  }

  // to handle well number that have the same well bottom profile image as the other
  switch (wellLabel) {
    case WellLabel.WELL_8:
      // well 8 have the same FLAT bottom profile as well 1
      if (wellBottomProfile === WellBottomProfile.FLAT) wellBottomPrefixName = '1';
      break;
    case WellLabel.WELL_IFC_192_24:
      wellBottomPrefixName = '192_24';
      break;
    case WellLabel.WELL_IFC_96_96:
      wellBottomPrefixName = '96_96';
      break;
    case WellLabel.WELL_IFC_48_48:
      wellBottomPrefixName = '48_48';
      break;
    case WellLabel.WELL_SPEAR_BIO:
      wellBottomPrefixName = '24_5';
      break;
    default:
      break;
  }

  useEffect(() => {
    // eslint-disable-next-line no-restricted-syntax
    for (const key of Object.keys(platePropertiesValue)) {
      if (parseFloat(platePropertiesValue[key]) === 0 || platePropertiesValue[key] === '') {
        if (
          (wellNumber === WellCount.WELL_1 && (key === 'columnPitch' || key === 'rowPitch')) ||
          (wellNumber === WellCount.WELL_8 && key === 'columnPitch') ||
          (wellNumber === WellCount.WELL_12 && key === 'rowPitch') ||
          ((plateType === PlateType.MICROPLATE || plateType === PlateType.SPEAR_BIO) && key === 'troughDepth') ||
          (wellLabel !== WellLabel.WELL_IFC_192_24 && key === 'colPitchAssaySample')
        ) {
          continue;
        }

        setIsError(true);

        return;
      }
    }

    setIsError(false);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [platePropertiesValue, wellNumber]);

  useEffect(() => {
    if (!isEdit && (wellLabel === WellLabel.WELL_24 || wellLabel === WellLabel.WELL_48)) {
      setEditModePlateProperties(true);
      setAdding24Or48WellPlate(true);
    }
  }, [wellLabel, isEdit, setEditModePlateProperties]);

  const handleUpdatePlateProperties = (key, value) => {
    let newPlatePorpertiesValue;

    if (
      (wellNumber === WellCount.WELL_96 || wellNumber === WellCount.WELL_384) &&
      (key === 'length' || key === 'width')
    ) {
      newPlatePorpertiesValue = {...platePropertiesValue, length: value, width: value};
    } else {
      newPlatePorpertiesValue = {...platePropertiesValue, [key]: value};
      if (wellNumber === WellCount.WELL_IFC_192_24 && key === 'depth') {
        newPlatePorpertiesValue = {...newPlatePorpertiesValue, troughDepth: value - 0.5};
      }
    }

    setPlatePropertiesValue(newPlatePorpertiesValue);

    if (isEdit) {
      handleEditPlateProperties(newPlatePorpertiesValue);
    }
  };

  const getPlatePropertiesComponent = () => {
    const propertiesComponent = [];
    const plateIdentifier = wellLabel || (wellNumber === WellCount.WELL_IFC_192_24 ? '192.24' : wellNumber.toString());
    const isTrough = TroughWellList.includes(plateIdentifier);

    ListsOfPlateProperties.forEach((el) => {
      // specific conditions to show the property
      const {showOnPlates, shownOnMicroplatesOnly, shownOnTroughOnly, shownOn, allExcluding} = el || {};

      // if there isn't any conditions specified, then by default the property will be shown to all plates
      const isShownOnAll = !(showOnPlates || shownOnMicroplatesOnly || shownOnTroughOnly || shownOn);

      const isPropertiesShown =
        isShownOnAll ||
        (el?.showOnPlates && el?.showOnPlates?.includes(plateType)) ||
        (el?.shownOnMicroplatesOnly && plateType === PlateType.MICROPLATE) ||
        (el?.shownOnTroughOnly && isTrough) ||
        (el?.shownOn && el?.shownOn?.includes(plateIdentifier));

      const isPlateExcluded = allExcluding && allExcluding?.includes(plateIdentifier);

      if (isPropertiesShown && !isPlateExcluded) {
        const {propertiesLabel: label, key, disabledOn, disabledAll} = el || {};
        const isDisabled = disabledAll || (disabledOn ? disabledOn.includes(plateIdentifier) : false);

        let propertyLabel = label;
        if (label === PlateProperties.INLET_DEPTH && plateType === PlateType.SPEAR_BIO) {
          propertyLabel = 'Well and Trough Depth';
        }
        if (label === PlateProperties.INLET_DIAMETER && plateType === PlateType.SPEAR_BIO) {
          propertyLabel = 'Well Diameter';
        }

        const propertyPlaceholder = adding24Or48WellPlate ? getPlatePropertyPlaceholder(label, wellLabel) : '';

        propertiesComponent.push(
          <ItemProperty
            label={propertyLabel}
            value={platePropertiesValue[key]}
            onUpdate={(newVal) => handleUpdatePlateProperties(key, newVal)}
            editMode={isEdit || editModePlateProperties}
            disabled={isDisabled || !isEditAllowed}
            isEditAllowed={isEditAllowed}
            placeholder={propertyPlaceholder}
            precision={PLATE_PROPERTIES_VALUE_PRECISION}
          />,
        );
      }
    });

    return propertiesComponent;
  };

  const closeContextMenuPlateDefinition = () => {
    setAnchorElMenu(null);
  };

  useClickOutside(contextMenuReteachRef, () => {
    closeContextMenuPlateDefinition();
  });

  const showContextMenuPlateDefinition = (e) => {
    setAnchorElMenu(e.currentTarget);
  };

  const listContextualMenuReteach = [
    {
      key: 1,
      id: 'auto',
      title: intl.formatMessage({id: 'modal.showContextMenuPlateDefinition.Auto'}),
      onClick: onReteachPlate,
      leftIcon: <FmlxIcon name="Setting" />,
    },
    {
      key: 2,
      id: 'manual',
      title: intl.formatMessage({id: 'modal.showContextMenuPlateDefinition.Manual'}),
      onClick: () => {
        onManualReteachPlate();
        closeContextMenuPlateDefinition();
      },
      leftIcon: <FmlxIcon name="Nt8JoystickSpeed" />,
    },
  ];

  const allowShowingManualMenuReteachPlate = useMemo(
    () =>
      ((aboutState.fastInstrumentType === '1' && loggedInUser.username === 'service') ||
        aboutState.fastInstrumentType === '2') &&
      wellLabel !== WellLabel.WELL_SPEAR_BIO,
    [aboutState.fastInstrumentType, loggedInUser.username, wellLabel],
  );

  return (
    <div id="plate-def-summary">
      <div className="plate-def-header">
        <div className="section-title bordered">Plate Definition Summary</div>
      </div>

      <div className="plate-def-content-container">
        <div className="plate-def-content-wrapper">
          <div className="section-additional-info italic">See the plate definition summary below.</div>
          <div className="image-container A-B">
            <img
              className={`image A is${wellNumberImageName}`}
              alt={`./svg/plate-type-icon/A_Step4_${wellNumberImageName}${wellShape}.svg`}
              src={`./svg/plate-type-icon/A_Step4_${wellNumberImageName}${wellShape}.svg`}
            />
            <img
              className={`image B is${wellBottomPrefixName}`}
              alt={`./svg/plate-type-icon/B_Step4_${wellBottomPrefixName}${wellBottomProfile}.svg`}
              src={`./svg/plate-type-icon/B_Step4_${wellBottomPrefixName}${wellBottomProfile}.svg`}
            />
            {wellNumber !== WellCount.WELL_1 && (
              <div>
                {wellNumber !== WellCount.WELL_8 && (
                  <div className={`summary-label col-picth ${classNameByWellNumber}`}>
                    {Number.isNaN(Number(columnPitch)) ? '' : columnPitch}
                  </div>
                )}
                {wellNumber !== WellCount.WELL_12 && plateType !== PlateType.SPEAR_BIO && (
                  <div className={`summary-label row-picth ${classNameByWellNumber}`}>
                    {Number.isNaN(Number(rowPitch)) ? '' : rowPitch}
                  </div>
                )}
              </div>
            )}
            <div className={`summary-label col-orign ${classNameByWellNumber}`}>
              {Number.isNaN(Number(columnOrigin)) ? '' : columnOrigin}
            </div>
            <div className={`summary-label row-orign ${classNameByWellNumber}`}>
              {Number.isNaN(Number(rowOrigin)) ? '' : rowOrigin}
            </div>
            {wellLabel === WellLabel.WELL_IFC_192_24 && (
              <div className={`summary-label col-pitch-assay-sample ${classNameByWellNumber} ${wellShape}`}>
                {Number.isNaN(Number(platePropertiesValue.colPitchAssaySample))
                  ? ''
                  : platePropertiesValue.colPitchAssaySample}
              </div>
            )}
            {plateType !== PlateType.IFC && plateType !== PlateType.SPEAR_BIO && (
              <div className={`summary-label length ${classNameByWellNumber} ${wellShape}`}>
                {Number.isNaN(Number(length)) ? '' : length}
              </div>
            )}
            {plateType === PlateType.SPEAR_BIO && (
              <div className={`summary-label width_1_2 ${classNameByWellNumber} ${wellShape}`}>15</div>
            )}
            {plateType === PlateType.SPEAR_BIO && (
              <div className={`summary-label width_3 ${classNameByWellNumber} ${wellShape}`}>4.5</div>
            )}
            {wellNumber === WellCount.WELL_96 ||
            wellNumber === WellCount.WELL_24 ||
            wellNumber === WellCount.WELL_48 ||
            wellNumber === WellCount.WELL_384 ||
            plateType === PlateType.SPEAR_BIO ||
            plateType === PlateType.IFC ? (
              <div className={`summary-label width ${classNameByWellNumber} ${wellShape}`}>
                {Number.isNaN(Number(length)) ? '' : length}
              </div>
            ) : (
              <div className={`summary-label width ${classNameByWellNumber} ${wellShape}`}>
                {Number.isNaN(Number(width)) ? '' : width}
              </div>
            )}
            <div className={`summary-label depth ${classNameByWellNumber}`}>
              {Number.isNaN(Number(depth)) ? '' : depth}
            </div>
            <div className={`summary-label height ${classNameByWellNumber}`}>
              {Number.isNaN(Number(height)) ? '' : height}
            </div>
          </div>
        </div>

        <div className="plate-measurement">
          {isEdit ? (
            <div className="update-plate-section">
              <h3>{intl.formatMessage({id: 'plateEditor.reteachPlate.title'})}</h3>
              <FmlxDivider />
              <FmlxButton
                label={intl.formatMessage({id: 'reteachPlate.button'})}
                withIcon="start"
                icon={<FmlxIcon name="FastPlateVertical" />}
                onClick={allowShowingManualMenuReteachPlate ? showContextMenuPlateDefinition : onReteachPlate}
                disabled={!isEditAllowed}
              />
              <FmlxPopover
                open={Boolean(anchorElMenu)}
                anchorEl={anchorElMenu}
                onClose={closeContextMenuPlateDefinition}
              >
                {listContextualMenuReteach.map((item) =>
                  // eslint-disable-next-line
                item.divider ? (
                    <FmlxDivider />
                  ) : item.htmlFor ? (
                    // eslint-disable-next-line
                        <label htmlFor={item.htmlFor}>
                      {/* eslint-disable-next-line react/jsx-props-no-spreading */}
                      <FmlxList {...item} />
                    </label>
                  ) : (
                    // eslint-disable-next-line react/jsx-props-no-spreading
                    <FmlxList {...item} />
                  ),
                )}
              </FmlxPopover>
            </div>
          ) : (
            ''
          )}
          <div className="section-title bordered">
            {isEdit ? (
              <span>Plate Properties</span>
            ) : (
              <>
                <span>Plate Properties</span>
                {/* eslint-disable-next-line no-nested-ternary */}
                {!editModePlateProperties ? (
                  <FmlxButton
                    size="sm"
                    label="EDIT"
                    variant="outline"
                    withIcon="start"
                    icon={<FmlxIcon name="Pencil" />}
                    onClick={() => setEditModePlateProperties(true)}
                  />
                ) : adding24Or48WellPlate ? (
                  <FmlxButton
                    size="sm"
                    label="CONFIRM"
                    icon={<FmlxIcon name="Pencil" />}
                    onClick={() => {
                      setEditModePlateProperties(false);
                      setAdding24Or48WellPlate(false);
                      handleEditPlateProperties(platePropertiesValue);
                    }}
                    disabled={isError}
                  />
                ) : (
                  <div style={{display: 'inline-flex', gap: '10px'}}>
                    <FmlxButton
                      size="sm"
                      label="UPDATE"
                      icon={<FmlxIcon name="Pencil" />}
                      onClick={() => {
                        setEditModePlateProperties(false);
                        handleEditPlateProperties(platePropertiesValue);
                      }}
                      disabled={isError}
                    />
                    <FmlxButton
                      size="sm"
                      variant="outline"
                      label="CANCEL"
                      icon={<FmlxIcon name="Pencil" />}
                      onClick={() => {
                        setEditModePlateProperties(false);
                        setPlatePropertiesValue({
                          height,
                          depth,
                          width,
                          length,
                          columnOrigin,
                          rowOrigin,
                          columnPitch,
                          rowPitch,
                          colPitchAssaySample,
                          troughDepth,
                        });
                      }}
                    />
                  </div>
                )}
              </>
            )}
          </div>
          {getPlatePropertiesComponent()}
        </div>
      </div>
    </div>
  );
}

function mapStateToProps(state) {
  return {
    loggedInUser: state.sessionState.user,
    aboutState: state.aboutState,
  };
}

function mapDispatchToProps(dispatch) {
  return {
    onReteachPlate: bindActionCreators(startReteachPlate, dispatch),
  };
}

export default connect(mapStateToProps, mapDispatchToProps)(withIntl(PlateViewSummaryInfo));

PlateViewSummaryInfo.propTypes = {
  wellNumberImageName: PropTypes.string.isRequired,
  wellNumber: PropTypes.number.isRequired,
  wellLabel: PropTypes.string.isRequired,
  wellBottomProfile: PropTypes.string.isRequired,
  wellShape: PropTypes.string.isRequired,
  plateType: PropTypes.string.isRequired,
  height: PropTypes.number.isRequired,
  depth: PropTypes.number.isRequired,
  width: PropTypes.number.isRequired,
  length: PropTypes.number.isRequired,
  columnOrigin: PropTypes.number.isRequired,
  rowOrigin: PropTypes.number.isRequired,
  columnPitch: PropTypes.number.isRequired,
  rowPitch: PropTypes.number.isRequired,
  colPitchAssaySample: PropTypes.number.isRequired,
  troughDepth: PropTypes.number.isRequired,
  handleEditPlateProperties: PropTypes.func.isRequired,
  setEditModePlateProperties: PropTypes.func.isRequired,
  editModePlateProperties: PropTypes.bool,
  isEdit: PropTypes.any.isRequired,
  isError: PropTypes.any.isRequired,
  setIsError: PropTypes.any.isRequired,
  onReteachPlate: PropTypes.any.isRequired,
  onManualReteachPlate: PropTypes.any.isRequired,
  intl: PropTypes.any.isRequired,
  isEditAllowed: PropTypes.bool.isRequired,
  loggedInUser: PropTypes.object.isRequired,
  aboutState: PropTypes.any.isRequired,
};

PlateViewSummaryInfo.defaultProps = {
  editModePlateProperties: false,
};
